<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode">
				<GlyphIcon :icon="GlyphIconName['power']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ data.title }}<span v-if="collapsed">&nbsp;({{ settings.variables.length }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<Button size="small" variant="text" class="header-button" @click="toggleCollapse()">
						<GlyphIcon :icon="GlyphIconName['chevron-up']" size="19" class="transition-transform" :class="{ 'upside-down': collapsed }" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="settings-box">
				<div>{{ t('switchvar.panelName') }}</div>
				<InputText maxlength="64" size="small" fluid v-model="settings.title" :required="true" />

				<div class="mt-2">{{ t('switchvar.editHeader') }}</div>
				<div>
					<Draggable :list="data.variables" item-key="_id" class="list-group" ghost-class="ghost" handle=".drag-handle">
						<template #item="{ element }">
							<div class="flex justify-between flex-col gap-1 mb-2 pb-2" style="border: 1px solid var(--p-form-field-border-color);border-radius:5px">

								<div class="flex justify-between items-center w-full drag-handle" style="background:var(--bg-color-dashboard-header);color:var(--txt-color-dashboard-header);height:20px;cursor:move;border-radius:5px 5px 0 0">
									<div class="flex items-center gap-1 pl-1">
										<div style="margin-top:2px;margin-right:3px"><i class="pi pi-bars" style="font-size:14px"></i></div>
										<div class="dotdotdot">{{ element.label || t('switchvar.newVariable') }}</div>
									</div>
									<div class="shrink-0">
										<Button size="small" severity="danger" class="dash-delete-btn" style="height:20px" @click="removeItem(element)">
											<GlyphIcon :icon="GlyphIconName['bin']" size="16" color="#fff" style="margin-top:1px" />
										</Button>
									</div>
								</div>

								<div class="w-full flex gap-3 pl-2 pr-2">
									<div class="w-1/2">
										<div>{{ t('switchvar.label') }}</div>
										<InputText maxlength="64" size="small" fluid v-model="element.label" />
									</div>
									<div class="w-1/2">
										<div>{{ t('switchvar.variableName') }}</div>
										<InputText :id="element._id" maxlength="64" size="small" fluid v-model="element.name" />
									</div>
								</div>

								<div class="w-full flex gap-3 pl-2 pr-2">
									<div class="w-1/2">
										<div>{{ t('switchvar.type') }}</div>
										<Select v-model="element.type" :options="types" size="small" fluid option-value="value" option-label="label" />
									</div>
									<div class="w-1/2">
										<div>{{ t('switchvar.owner') }}</div>
										<Select v-model="element.owner" :options="ownerTypes" size="small" fluid option-value="value" option-label="label" />
									</div>
								</div>

								<div v-if="element.type === 'VARIABLE'" class="w-full flex flex-col mt-1 pl-2 pr-2">
									<div>{{ t('switchvar.values') }}</div>
									<div class="flex justify-between items-center mb-1" v-for="(val,index) in element.values" :key="index">
										<div class="flex gap-2 w-full items-center">
											<Button class="shrink-0" style="width:22px;height:25px" size="small" severity="danger" @click="removeValue(element, index)">
												<GlyphIcon :icon="GlyphIconName['bin']" size="15" color="#fff" style="margin-top:1px" />
											</Button>
											<InputText fluid maxlength="64" size="small" :placeholder="t('switchvar.value')" v-model="element.values[index]" />
										</div>
									</div>
									<div v-if="element.values.length === 0" class="text-center italic mb-1 p-1 text-sm text-muted-color" style="background:#e0e0e0;border: 1px solid var(--p-form-field-border-color);border-radius:5px">{{ t('switchvar.noValues') }}</div>

									<Button fluid severity="info" size="small" @click="addValue(element)">{{ t('switchvar.addValue') }}</Button>
								</div>
							</div>
						</template>
					</Draggable>

					<div v-if="settings.variables.length === 0" class="text-center italic mb-1 p-1 text-sm text-muted-color" style="background:#e0e0e0;border: 1px solid var(--p-form-field-border-color);border-radius:5px">{{ t('switchvar.noVariables') }}</div>
				</div>

				<Button fluid severity="info" size="small" @click="addItem()">{{ t('switchvar.add') }}</Button>

			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode&&loaded" class="flex flex-col" :id="'SV-'+data.column+'-'+data.position" :style="{'max-height': collapsed ? '0px': (settings.variables.length * 40) + 'px'}" style="overflow:auto;transition:400ms;">
				<div v-for="item in data.variables" :key="(item as Item)._id" class="flex justify-between items-center gap-1" style="min-height:38px;padding: 2px 4px 2px 4px;border-bottom: 1px solid var(--p-form-field-border-color)">
					<div class="dotdotdot">{{ item.label }}</div>
					<ToggleSwitch v-if="item.type === 'SWITCH'" v-model="values[getName(item)]" size="small" @value-change="changed($event, item as Item)" />
					<Select v-if="item.type === 'VARIABLE'" v-model="values[getName(item)]" size="small" variant="outlined" :options="item.values" @change="changed($event, item as Item)" />
				</div>
			</div>
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useApiStore } from '@/stores/Api'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useRealtimeStore } from '@/stores/Realtime'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import Draggable from 'vuedraggable'
import type { PbxAppDashboardItemSwitchVar, PbxAppDashboardItemSwitchVarVariable } from '@/TcxApi/PbxApp'
import { onBeforeUnmount, ref, watch } from 'vue'
import { randomString } from '@/utilities/Tools'
import type { PbxEventSwitch, PbxEventVariable } from '@per_moeller/tcx-realtime-client'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import type { SelectChangeEvent } from 'primevue/select'

const ss = useSettingsStore()
const api = useApiStore()
const dashboards = useDashboardsStore()
const { t } = useI18n()
const realtime = useRealtimeStore()

interface Props {
	data: PbxAppDashboardItemSwitchVar
}

interface Item extends PbxAppDashboardItemSwitchVarVariable {
	_id: string
}

const props = defineProps<Props>()
const isDraggable = ref(false)
const types = ref([
	{ value: 'SWITCH', label: t('switchvar.switch') },
	{ value: 'VARIABLE', label: t('switchvar.variable') }
])
const ownerTypes = ref([
	{ value: 'PERSONAL', label: t('switchvar.personal') },
	{ value: 'SHARED', label: t('switchvar.shared') },
	{ value: 'SYSTEM', label: t('switchvar.system') }
])
const values = ref<Record<string, string | boolean>>({})
const settings = dashboards.dashboard?.items.find(item => item.column === props.data.column && item.position === props.data.position) as PbxAppDashboardItemSwitchVar
const collapsed = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`) || false)
const loaded = ref(false)

function getName(item: PbxAppDashboardItemSwitchVarVariable) {
	switch (item.owner) {
		case 'SHARED':
			return 'SHARED:' + item.name
		case 'SYSTEM':
			return item.name
		default:
			return ss.auth?.employee + ':' + item.name
	}
}

function remove() {
	dashboards.removeItem(settings.column, settings.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function toggleCollapse() {
	collapsed.value = !collapsed.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`, collapsed.value)
}

function removeItem(item: Item) {
	const index = settings.variables.findIndex(sw => (sw as Item)._id === item._id)
	if (index !== -1) {
		settings.variables.splice(index, 1)
	}
}

function addItem() {
	const id = randomString(32);

	(settings.variables as Item[]).push({ _id: id, type: 'SWITCH', name: '', owner: 'PERSONAL', label: '', values: [] })

	setTimeout(() => {
		document.getElementById(id)?.focus()
	}, 100)
}

function addValue(item: Item) {
	item.values?.push('')
}

function removeValue(item: Item, index: number) {
	item.values?.splice(index, 1)
}

async function changed(newValue: string | boolean | SelectChangeEvent, item: Item) {
	if (typeof newValue === 'object') {
		newValue = (newValue as SelectChangeEvent).value
	}

	const prefix = item.owner === 'SHARED' ? 'SHARED:' : item.owner === 'SYSTEM' ? '' : ss.auth?.employee + ':' // NOSONAR

	if (item.type === 'SWITCH') {
		try { await api.post(`/pbx/switch/${prefix}${item.name}`, { value: newValue }) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('switchvar.setErrorHeader'), message: t('switchvar.setSwitchErrorMessage') })
			loadValues()
		}
	} else if (item.type === 'VARIABLE') {
		try { await api.post(`/pbx/variable/${prefix}${item.name}`, { value: newValue }) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('switchvar.setErrorHeader'), message: t('switchvar.setVariableErrorMessage') })
			loadValues()
		}
	}
}

function switchOrVariableEvent(data: PbxEventVariable | PbxEventSwitch) {
	if (data.name.startsWith('SHARED:')) {
		// Shared
		if (settings.variables.find(variable => variable.name === data.name.substring(7) && variable.owner === 'SHARED')) {
			values.value[data.name] = data.value
		}
	} else if (/^[a-fA-F0-9]{24}:/.test(data.name)) {
		// Personal
		if (settings.variables.find(variable => variable.name === data.name.substring(25) && variable.owner === 'PERSONAL' && data.name.substring(0, 24) === ss.auth?.employee)) {
			values.value[data.name] = data.value
		}
	} else {
		// Global
		if (settings.variables.find(variable => variable.name === data.name && variable.owner === 'SYSTEM')) { // NOSONAR
			values.value[data.name] = data.value
		}
	}
}

async function loadValues() {
	const promises = []

	for (const item of settings.variables) {
		const prefix = item.owner === 'SHARED' ? 'SHARED:' : item.owner === 'SYSTEM' ? '' : ss.auth?.employee + ':' // NOSONAR
		const id = prefix + item.name

		const promise = api.get<{ name: string, value: string }>(`/pbx/${item.type.toLowerCase()}/${id}`)
			.then(res => {
				values.value[id] = res.data.value
			})
			.catch(() => {
				values.value[id] = item.type === 'SWITCH' ? false : ''
			})

		promises.push(promise)

		Promise.allSettled(promises).then(() => {
			loaded.value = true
		})
	}
}

for (const item of settings.variables) {
	(item as Item)._id = randomString(32)
}

loadValues()
realtime.pbxEvents.on('VARIABLE', switchOrVariableEvent)
realtime.pbxEvents.on('SWITCH', switchOrVariableEvent)

onBeforeUnmount(() => {
	realtime.pbxEvents.off('VARIABLE', switchOrVariableEvent)
	realtime.pbxEvents.off('SWITCH', switchOrVariableEvent)
})

watch(() => dashboards.editMode, (newValue) => {
	if (newValue) {
		loaded.value = false
	} else {
		loadValues()
	}
})
</script>

<style scoped>
	.edit-title {
		color: var(--p-text-color);
		font-size: 12px;
		font-weight: 400;
	}
	.value {
		align-self: center;
		height: 25px;
	}
	.settings-box {
		margin: 5px 10px;
		padding-bottom: 4px;
	}
	.ghost {
		opacity: 0.25;
	}
	.delete-item-btn {
		padding: 0 2px 2px 2px;
		margin-right: 5px;
	}
	.employee-item {
		padding: 2px 5px 2px 5px !important;
	}
</style>
